import { BaseReport, type ReportRunDeps, type SortAccessor } from './baseReport.js';
import type { ReportCache } from './reportCache.js';
import type { ApiClient } from './runtime.js';
import type { CampaignDeliveryRow, DeliveryMetricKey, ReportDom } from './types.js';
import { csvVal, downloadCsv, esc, formatHeader, isGroupLast } from './utils.js';

const PRIMARY_DELIVERY_METRIC_KEYS: readonly DeliveryMetricKey[] = [
  'created',
  'sent',
  'delivered',
  'opened',
  'clicked',
  'unsubscribed',
];

const ALL_DELIVERY_METRIC_KEYS: readonly DeliveryMetricKey[] = [
  '2xx',
  '3xx',
  '4xx',
  '5xx',
  'attempted',
  'bounced',
  'clicked',
  'human_clicked',
  'prefetch_clicked',
  'converted',
  'created',
  'deferred',
  'delivered',
  'drafted',
  'failed',
  'opened',
  'human_opened',
  'prefetch_opened',
  'sent',
  'spammed',
  'suppressed',
  'undeliverable',
  'topic_unsubscribed',
  'unsubscribed',
];

export class CampaignDeliveryReport extends BaseReport<CampaignDeliveryRow> {
  private filterValue = '';
  private hideZero = true;
  private hours = 4;

  constructor(cache: ReportCache, apiClient: ApiClient, dom: ReportDom, deps: ReportRunDeps) {
    super('campaign-delivery', cache, apiClient, dom, deps);
  }

  override get reportLabel(): string {
    return 'Campaign Delivery';
  }

  setHours(hours: number): void {
    this.hours = hours;
  }

  getHours(): number {
    return this.hours;
  }

  protected override buildCacheKey(slugs: string[]): string {
    return `campaign-delivery|${[...slugs].sort().join(',')}|${this.hours}`;
  }

  protected override fetchData(slugs: string[]): Promise<CampaignDeliveryRow[]> {
    return this.apiClient.getCampaignDelivery(slugs, this.hours);
  }

  protected override resetFilterState(): void {
    this.filterValue = '';
    this.hideZero = true;
  }

  protected override peopleAccessor(): (r: CampaignDeliveryRow) => number {
    return (r) => r.delivery_metrics?.sent ?? 0;
  }

  protected override channelAccessor(): (r: CampaignDeliveryRow) => string {
    return (r) => r.channel;
  }

  protected override summaryItemLabel(): string {
    return 'num_campaigns';
  }

  protected override filterRows(rows: CampaignDeliveryRow[]): CampaignDeliveryRow[] {
    return rows.filter((r) => {
      if (!(r.campaign_name || '').toLowerCase().includes(this.filterValue)) return false;
      if (this.hideZero && isAllZeroPrimary(r)) return false;
      return true;
    });
  }

  protected override renderTitle(rows: CampaignDeliveryRow[], filtered: CampaignDeliveryRow[]): void {
    const totalSent = filtered.reduce(
      (sum, r) => sum + (r.delivery_metrics?.sent ?? 0),
      0,
    );
    this.dom.title.innerHTML =
      `Campaign Delivery Report  —  last ${this.hours}h  —  ${filtered.length} of ${rows.length} active campaign${rows.length !== 1 ? 's' : ''} ` +
      `<span class="title-people">(${totalSent.toLocaleString()} sent)</span>` +
      this.reportTimestampHtml();
  }

  protected override renderToolbar(toolbar: HTMLElement): void {
    toolbar.innerHTML = `
      <input type="text" id="campaign-delivery-filter" placeholder="Filter by campaign name…" />
      <label class="toggle-label">
        <input type="checkbox" id="hide-zero-delivery-toggle" ${this.hideZero ? 'checked' : ''} />
        Hide zero counts
      </label>
    `;

    const filterEl = toolbar.querySelector<HTMLInputElement>('#campaign-delivery-filter')!;
    filterEl.value = this.filterValue;
    filterEl.addEventListener('input', () => {
      this.filterValue = filterEl.value.toLowerCase();
      this.refreshView();
    });

    const zeroToggle = toolbar.querySelector<HTMLInputElement>('#hide-zero-delivery-toggle')!;
    zeroToggle.addEventListener('change', () => {
      this.hideZero = zeroToggle.checked;
      this.refreshView();
    });
  }

  protected override renderData(
    area: HTMLElement,
    rows: CampaignDeliveryRow[],
    filtered: CampaignDeliveryRow[],
  ): void {
    this.appendClientSummary(area, filtered);

    if (filtered.length === 0) {
      const msg = document.createElement('div');
      msg.className = 'empty-msg';
      msg.textContent =
        rows.length === 0
          ? 'No active campaigns found for the selected clients.'
          : 'No campaigns match your filter.';
      area.appendChild(msg);
      return;
    }

    const sorted = this.sortRows(filtered, deliveryAccessor);
    const windowEnd = this.lastCachedAt ?? this.lastRanAt ?? new Date();
    const windowStart = new Date(windowEnd.getTime() - this.hours * 3600 * 1000);

    const table = document.createElement('table');
    table.innerHTML = `
      <thead>
        <tr>
          ${this.thSortable('client_slug', 'Client')}
          ${this.thSortable('campaign_id', 'Campaign ID', 'right')}
          ${this.thSortable('campaign_name', 'Campaign')}
          ${this.thSortable('channel', 'Channel')}
          ${PRIMARY_DELIVERY_METRIC_KEYS.map((k) => this.thSortable(k, formatHeader(k), 'right')).join('')}
        </tr>
      </thead>
      <tbody>
        ${sorted
          .map((r, i) => {
            const url = campaignDeliveryUrl(r.workspace_id, r.campaign_id, windowStart, windowEnd);
            return `
          <tr${isGroupLast(sorted, i, 'client_slug') ? ' class="client-group-last"' : ''}>
            <td>${esc(r.client_slug)}</td>
            <td class="num"><a href="${esc(url)}" target="_blank" rel="noopener">${esc(r.campaign_id)}</a></td>
            <td><a href="${esc(url)}" target="_blank" rel="noopener">${esc(r.campaign_name)}</a></td>
            <td>${esc(r.channel)}</td>
            ${PRIMARY_DELIVERY_METRIC_KEYS.map(
              (k) => `<td class="num">${(r.delivery_metrics?.[k] ?? 0).toLocaleString()}</td>`,
            ).join('')}
          </tr>`;
          })
          .join('')}
      </tbody>`;

    area.appendChild(table);
    this.bindSortHeaders(table);
  }

  override exportCsv(): void {
    if (!this.lastData || this.lastData.length === 0) return;
    const filtered = this.filterRows(this.lastData);
    const visible = this.sortRows(filtered, deliveryAccessor);
    const windowEnd = this.lastCachedAt ?? this.lastRanAt ?? new Date();
    const windowStart = new Date(windowEnd.getTime() - this.hours * 3600 * 1000);

    let csv = `Client,Campaign ID,Campaign,Channel,${ALL_DELIVERY_METRIC_KEYS.map(formatHeader).join(',')},Link\n`;
    csv += visible
      .map(
        (r) =>
          `${csvVal(r.client_slug)},${r.campaign_id},${csvVal(r.campaign_name)},${csvVal(r.channel)},${ALL_DELIVERY_METRIC_KEYS.map((k) => r.delivery_metrics?.[k] ?? 0).join(',')},${csvVal(campaignDeliveryUrl(r.workspace_id, r.campaign_id, windowStart, windowEnd))}`,
      )
      .join('\n');

    downloadCsv(csv, this.reportType);
  }
}

function isAllZeroPrimary(r: CampaignDeliveryRow): boolean {
  return PRIMARY_DELIVERY_METRIC_KEYS.every((k) => (r.delivery_metrics?.[k] ?? 0) === 0);
}

function campaignDeliveryUrl(
  workspaceId: number | string,
  campaignId: number,
  start: Date | string,
  end: Date | string,
): string {
  const startDate = start instanceof Date ? start : new Date(start);
  const endDate = end instanceof Date ? end : new Date(end);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    throw new Error('campaignDeliveryUrl requires valid query window dates');
  }
  const pad = (n: number) => String(n).padStart(2, '0');
  const fmt = (d: Date) =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const params = new URLSearchParams({
    end_time: fmt(endDate),
    message_channels: 'email_twilio_webhook_slack',
    pd_channel: 'email',
    start_time: fmt(startDate),
    unit: 'count',
  });
  return `https://fly.customer.io/workspaces/${encodeURIComponent(String(workspaceId))}/journeys/campaigns/${encodeURIComponent(String(campaignId))}/overview/metrics?${params.toString()}`;
}

const deliveryAccessor: SortAccessor<CampaignDeliveryRow> = (key) => {
  if (key === 'client_slug') return (r) => r.client_slug;
  if (key === 'campaign_id') return (r) => r.campaign_id;
  if (key === 'campaign_name') return (r) => r.campaign_name;
  if (key === 'channel') return (r) => r.channel;
  if ((ALL_DELIVERY_METRIC_KEYS as readonly string[]).includes(key)) {
    return (r) => r.delivery_metrics?.[key as DeliveryMetricKey] ?? 0;
  }
  return () => '';
};
